import { Annotation } from '../Annotation';
import { BoxVolume, SphereVolume } from '../utils/Volume';

function createPointcloudData(pointcloud) {
  let material = pointcloud.material;

  let ranges = [];

  for (let [name, value] of material.ranges) {
    ranges.push({
      name: name,
      value: value,
    });
  }

  if (typeof material.elevationRange[0] === 'number') {
    ranges.push({
      name: 'elevationRange',
      value: material.elevationRange,
    });
  }
  if (typeof material.intensityRange[0] === 'number') {
    ranges.push({
      name: 'intensityRange',
      value: material.intensityRange,
    });
  }

  let pointSizeTypeName = Object.entries(Potree.PointSizeType).find(
    (e) => e[1] === material.pointSizeType
  )[0];

  let pointShapeName = Object.entries(Potree.PointShape).find(
    (e) => e[1] === material.shape
  )[0];

  let jsonMaterial = {
    activeAttributeName: material.activeAttributeName,
    ranges: ranges,
    size: material.size,
    minSize: material.minSize,
    pointSizeType: pointSizeTypeName,
    pointShape: pointShapeName,
    matcap: material.matcap,
  };

  const pcdata = {
    name: pointcloud.name,
    url: pointcloud.pcoGeometry.url,
    position: pointcloud.position.toArray(),
    rotation: pointcloud.rotation.toArray(),
    scale: pointcloud.scale.toArray(),
    material: jsonMaterial,
  };

  return pcdata;
}

function createProfileData(profile) {
  const data = {
    uuid: profile.uuid,
    name: profile.name,
    points: profile.points.map((p) => p.toArray()),
    height: profile.height,
    width: profile.width,
  };

  return data;
}

function createVolumeData(volume) {
  const data = {
    uuid: volume.uuid,
    type: volume.type || volume.constructor.name,
    name: volume.name,
    position: volume.position.toArray(),
    rotation: volume.rotation.toArray(),
    scale: volume.scale.toArray(),
    visible: volume.visible,
    clip: volume.clip,
    folder: volume.folder,
  };

  return data;
}

function createVolumesData(viewer) {
  return viewer.scene.volumes
    .filter((v) => v instanceof BoxVolume || v instanceof SphereVolume)
    .map(createVolumeData);
}

function createCameraAnimationData(animation) {
  const controlPoints = animation.controlPoints.map((cp) => {
    const cpdata = {
      position: cp.position.toArray(),
      target: cp.target.toArray(),
    };

    return cpdata;
  });

  const data = {
    uuid: animation.uuid,
    name: animation.name,
    duration: animation.duration,
    t: animation.t,
    curveType: animation.curveType,
    visible: animation.visible,
    controlPoints: controlPoints,
  };

  return data;
}

function createMeasurementData(measurement) {
  const data = {
    uuid: measurement.uuid,
    name: measurement.name,
    points: measurement.points.map((p) => p.position.toArray()),
    showDistances: measurement.showDistances,
    showCoordinates: measurement.showCoordinates,
    showArea: measurement.showArea,
    closed: measurement.closed,
    showAngles: measurement.showAngles,
    showHeight: measurement.showHeight,
    showCircle: measurement.showCircle,
    showAzimuth: measurement.showAzimuth,
    showEdges: measurement.showEdges,
    color: measurement.color.toArray(),
    folder: measurement.folder,
  };

  return data;
}

function createMeasurementsData(viewer) {
  return viewer.scene.measurements
    .filter((m) => !(m instanceof BoxVolume || m instanceof SphereVolume))
    .map(createMeasurementData);
}

function createOrientedImagesData(images) {
  const data = {
    cameraParamsPath: images.cameraParamsPath,
    imageParamsPath: images.imageParamsPath,
  };

  return data;
}

function createGeopackageData(geopackage) {
  const data = {
    path: geopackage.path,
  };

  return data;
}

function createAnnotationData(annotation) {
  const data = {
    uuid: annotation.uuid,
    title: annotation.title.toString(),
    description: annotation.description,
    position: annotation.position.toArray(),
    offset: annotation.offset.toArray(),
    children: [],
    folder: annotation.folder,
  };
  if (annotation.cameraPosition) {
    data.cameraPosition = annotation.cameraPosition;
    if (
      annotation.cameraPosition.x === 0 &&
      annotation.cameraPosition.y === 0 &&
      annotation.cameraPosition.z === 0
    ) {
      data.cameraPosition = null;
    }
  }

  if (annotation.cameraTarget) {
    data.cameraTarget = annotation.cameraTarget;
    if (
      annotation.cameraTarget.x === 0 &&
      annotation.cameraTarget.y === 0 &&
      annotation.cameraTarget.z === 0
    ) {
      data.cameraTarget = null;
    }
  }

  if (typeof annotation.radius !== 'undefined') {
    data.radius = annotation.radius;
  }

  return data;
}

function createAnnotationsData(viewer) {
  const root = viewer.scene.annotations;
  const map = new Map();
  const loaded = [];

  const visit = (annotation) => {
    if (annotation !== root) {
      const data = createAnnotationData(annotation);
      map.set(annotation, data);
      loaded.push(annotation);
    }
    for (const child of annotation.children) visit(child);
  };
  visit(root);

  for (const [annotation, data] of map) {
    for (const child of annotation.children) {
      const childData = map.get(child);
      if (childData) data.children.push(childData);
    }
  }

  return loaded.map((a) => map.get(a));
}

function createSettingsData(viewer) {
  const clipMethodName = Object.entries(Potree.ClipMethod).find(
    (e) => e[1] === viewer.getClipMethod()
  )[0];
  const clipTaskName = Object.entries(Potree.ClipTask).find(
    (e) => e[1] === viewer.getClipTask()
  )[0];

  return {
    pointBudget: viewer.getPointBudget(),
    fov: viewer.getFOV(),
    edlEnabled: viewer.getEDLEnabled(),
    edlRadius: viewer.getEDLRadius(),
    edlStrength: viewer.getEDLStrength(),
    background: viewer.getBackground(),
    clipMethod: clipMethodName,
    clipTask: clipTaskName,
    minNodeSize: viewer.getMinNodeSize(),
    showBoundingBoxes: viewer.getShowBoundingBox(),
  };
}

function createSceneContentData(viewer) {
  const data = [];

  const potreeObjects = [];

  viewer.scene.scene.traverse((node) => {
    if (node.potree) {
      potreeObjects.push(node);
    }
  });

  for (const object of potreeObjects) {
    if (object.potree.file) {
      const saveObject = {
        file: object.potree.file,
      };

      data.push(saveObject);
    }
  }

  return data;
}

function createViewData(viewer) {
  const view = viewer.scene.view;

  const data = {
    position: view.position.toArray(),
    target: view.getPivot().toArray(),
  };

  return data;
}

function createClassificationData(viewer) {
  const classifications = viewer.classifications;

  const data = classifications;

  return data;
}

export function saveProject(viewer) {
  const scene = viewer.scene;

  const data = {
    type: 'Potree',
    version: 1.7,
    settings: createSettingsData(viewer),
    view: createViewData(viewer),
    classification: createClassificationData(viewer),
    pointclouds: scene.pointclouds.map(createPointcloudData),
    measurements: createMeasurementsData(viewer),
    volumes: createVolumesData(viewer),
    cameraAnimations: scene.cameraAnimations.map(createCameraAnimationData),
    profiles: scene.profiles.map(createProfileData),
    annotations: createAnnotationsData(viewer),
    orientedImages: scene.orientedImages.map(createOrientedImagesData),
    geopackages: scene.geopackages.map(createGeopackageData),
    // objects: createSceneContentData(viewer),
  };

  return data;
}
